// borefieldFacade — a READ-ONLY legacy-Borefield view over the canonical spine store, plus the
// write-redirect that keeps the spine the single writable model.
//
// Bore→Well cutover Phase 2 (facade-over-spine). Legacy consumers that still speak the Borefield/Bore
// shape (useBorefieldData + the borefield-reading screens) read views PROJECTED from the spine
// (pads + wells) via the reviewed `padToBorefieldView`; their writes MIGRATE the whole Borefield back
// into the spine via the reviewed `migrateBorefieldsToWells`. There is exactly ONE writable model —
// the spine — so the epic §6 Sev-1 (two writable models drifting) cannot occur.
//
// NO new migration/engineering math (Charter Abs-2 + the cross-implementation parity rule): this file
// only ORCHESTRATES the two already-adversarially-reviewed adapters — it re-implements neither.

import { useMemo, useSyncExternalStore } from "react";
import { padsStore, wellsStore, padJobsStore } from "./spineStore";
import {
  migrateBorefieldsToWells,
  padToBorefieldView,
} from "../migrateBorefields";
import type { Borefield } from "../../borefields/types";
import type { Pad, Well } from "../types";
import type { PadJob } from "../../jobs/types";

/** Project the spine (pads + wells) into legacy Borefield views. ONLY carry-bearing pads (those
 *  migrated from a legacy Borefield, so they hold a `legacyBorefield`) can be projected —
 *  wizard-created pads have no Borefield form and are omitted (the documented `padToBorefieldView`
 *  facade contract, NOT silent loss: they remain in the spine and are reached via the Well UI). */
export function projectBorefields(
  pads: readonly Pad[],
  wells: readonly Well[],
): Borefield[] {
  const out: Borefield[] = [];
  for (const pad of pads) {
    if (pad.legacyBorefield === undefined) continue; // wizard-native → no legacy Borefield view
    out.push(padToBorefieldView(pad, wells));
  }
  return out;
}

/** Reactive Borefield views over the current spine. Re-renders on any spine write (same-tab or
 *  cross-tab). Memoized so the `padToBorefieldView` clone is paid once per store change, not per
 *  render (the 2c memoization requirement). This is the read side of the facade — the replacement for
 *  `useSelector(selectLocalBorefields)` in the legacy borefield-reading consumers. */
export function useBorefieldViews(): Borefield[] {
  const pads = useSyncExternalStore(
    padsStore.subscribe,
    padsStore.read,
    padsStore.read,
  );
  const wells = useSyncExternalStore(
    wellsStore.subscribe,
    wellsStore.read,
    wellsStore.read,
  );
  return useMemo(() => projectBorefields(pads, wells), [pads, wells]);
}

/** SEV-1 FIX (Bore→Well cutover Phase 6 Inc 1b, GENERALIZED in Inc 1). A facade round-trip re-derives a
 *  Pad/Well from its LOSSY Borefield/Bore projection, which reproduces ONLY the handful of fields
 *  `borefieldToPad`/`boreToWell` know (id, name, `operatorOfRecord`, `legacyBorefield`, audit stamps).
 *  Every other spine-NATIVE field — Pad `businessUnit`/`currency`/`padAfe`/`legalLocation`/…, Well
 *  `numAPI`/`nameLegal`/`afeNumber`/`wellheadElevation`(+Unit)/… — is set spine-DIRECT (e.g.
 *  `PadCreationWizard` edit → `updatePad`) on a record that stays carry-bearing, so it has NO home in
 *  the projection. The old 3-field version (archivedAt/createdAt/createdBy only) still SILENTLY DROPPED
 *  ~40 such fields on re-upsert — Abs-7 loss of money (`afeNumber`), legal identity (`numAPI`/
 *  `nameLegal`) and UOM-bearing (`wellheadElevation`) data.
 *
 *  EXCEPTION — `surfaceLatitude`/`surfaceLongitude`: the coord phase made the migration REPRODUCE these
 *  (from the bore/anchor coord), so `...incoming` carries the ORIGINAL and would CLOBBER a spine-direct
 *  correction — the opposite failure mode from the dropped fields. They are therefore RE-ASSERTED from
 *  prior below (like the audit fields), not merely preserved by `...prior`.
 *
 *  So the PRIOR stored record is the BASE and the migration's reproduced fields OVERLAY it:
 *    - `...prior`    preserves every PAD/WELL-RECORD-level spine-only field the projection can't
 *      reproduce (Abs-7; strictly additive at that level — no prior top-level value is dropped).
 *    - `...incoming` overlays the migration's reproduced/edited fields (name, `operatorOfRecord`,
 *      `legacyBorefield`, and — for a Well — the `wellbores` array WHOLESALE, which carries the
 *      JUST-EDITED report/survey plus each wellbore's `legacyBore` carry, so the edit WINS). CAVEAT:
 *      because `wellbores` is replaced wholesale, a WELLBORE-level spine-only field NOT reproduced by the
 *      `legacyBore` carry (a spine-direct `wellboreStatus`/`actualDepth`/rename) would reset on a
 *      round-trip. Latent today (only `jobs` are mutated on carry-bearing wellbores, and jobs survive);
 *      the whole facade retires in Phase 7.
 *    - the audit/archive invariants are RE-ASSERTED from prior (authoritative): `incoming` may ALSO carry
 *      createdAt/createdBy read back from the (original) legacy carry, which can be STALER than a
 *      spine-direct edit — so prior wins; `modifiedAt` is always bumped (a re-migration IS a modification).
 *
 *  `{...prior, ...incoming}` is SAFE (verified against `borefieldToPad`/`boreToWell`): both set ONLY the
 *  keys they know and emit every optional field via `...(x ? {…} : {})`, so a spine-only key is ABSENT on
 *  `incoming` (never present-as-`undefined`) — the overlay cannot clobber a prior value with `undefined`.
 *  exactOptionalPropertyTypes-safe: only present prior values are spread for the audit re-assertion.
 *  Applied ONLY to incoming migrated records (matched by id to an existing one); wizard-native
 *  (carry-less) records never enter `newPads`/`newWells`, so they are passed through untouched by the
 *  preserve filters below. */
function withPriorAudit<
  T extends {
    archivedAt?: string;
    createdAt?: string;
    createdBy?: string;
    modifiedAt?: string;
    modifiedBy?: string;
    surfaceLatitude?: string;
    surfaceLongitude?: string;
  },
>(incoming: T, prior: T | undefined): T {
  if (!prior) return incoming; // a brand-new (create) record — nothing prior to preserve
  return {
    ...prior, // preserve every Pad/Well-level spine-only field the lossy projection can't reproduce (Abs-7)
    ...incoming, // overlay the migration's reproduced/edited fields (name, wellbores, operatorOfRecord, legacyBorefield, …)
    // Re-assert the audit/archive invariants — prior is authoritative; the migration can't hold these
    // (incoming carries archived=none + created/modified read back from the ORIGINAL legacy carry).
    ...(prior.archivedAt ? { archivedAt: prior.archivedAt } : {}),
    ...(prior.createdAt ? { createdAt: prior.createdAt } : {}),
    ...(prior.createdBy ? { createdBy: prior.createdBy } : {}),
    // Keep the last real modifier (prior), NOT the original carry author, alongside the fresh bump —
    // otherwise the record reads "modified now by <original author>" (audit incoherence).
    ...(prior.modifiedBy ? { modifiedBy: prior.modifiedBy } : {}),
    // Re-assert surfaceLatitude/Longitude from prior — UNLIKE most spine-only fields, the coord phase
    // made the migration REPRODUCE these (boreToWell / borefieldToPad from the bore/anchor coord), so
    // `...incoming` carries the ORIGINAL and would otherwise clobber a spine-direct correction (a
    // re-survey / pad-marker move via the wizard → updateWell/updatePad). Prior is authoritative, exactly
    // like the audit fields; on the FIRST migration prior is undefined (handled above) so incoming seeds
    // them. (Abs-7: a facade round-trip must not silently revert an edited wellhead coordinate.)
    ...(prior.surfaceLatitude
      ? { surfaceLatitude: prior.surfaceLatitude }
      : {}),
    ...(prior.surfaceLongitude
      ? { surfaceLongitude: prior.surfaceLongitude }
      : {}),
    modifiedAt: new Date().toISOString(),
  };
}

/** Write-redirect (bulk): UPSERT whole Borefields into the spine — migrate each to its pad + wells,
 *  then replace those pads and their wells while LEAVING every other pad/well (e.g. wizard-created)
 *  untouched. Handles create (new pad), update (changed wells), and delete-bore (a removed bore simply
 *  isn't in the migrated wells). Merge, never replace-all — so seeding demo data can't wipe the spine.
 *  Each replaced record carries ALL its prior spine-only fields forward (withPriorAudit) so a
 *  re-migration can't drop spine-native data (afeNumber/numAPI/wellheadElevation/…) or un-archive/
 *  audit-reset an existing pad/well (Inc 1 Sev-1). */
export function upsertBorefieldsIntoSpine(
  borefields: readonly Borefield[],
): void {
  const { pads: newPads, wells: newWells } =
    migrateBorefieldsToWells(borefields);
  const incomingPadIds = new Set(newPads.map((p) => p.id));

  const curPads = padsStore.read();
  // id→existing map: this is the ONLY site holding BOTH the incoming (re-migrated) and the prior stored
  // record, so it is the correct home to preserve ALL spine-only fields (the projection can't reproduce).
  const priorPadById = new Map(curPads.map((p) => [p.id, p]));
  padsStore.setAll([
    ...curPads.filter((p) => !incomingPadIds.has(p.id)),
    ...newPads.map((p) => withPriorAudit(p, priorPadById.get(p.id))),
  ]);

  const curWells = wellsStore.read();
  const priorWellById = new Map(curWells.map((w) => [w.id, w]));
  // Preserve wizard-native (carry-less) wells attached to an incoming (migrated) pad. The Borefield
  // projection OMITS them (they have no `legacyBore` carry), so the re-migrated `newWells` can't
  // reproduce them — filtering out ALL of a pad's wells would silently drop them (Charter Abs-7, no
  // silent destruction). Only the carry-bearing wells the projection actually represents are replaced.
  const isCarryBearing = (w: Well): boolean =>
    w.wellbores?.some((wb) => wb.legacyBore !== undefined) ?? false;
  wellsStore.setAll([
    ...curWells.filter(
      (w) => !incomingPadIds.has(w.fieldPadId) || !isCarryBearing(w),
    ),
    ...newWells.map((w) => withPriorAudit(w, priorWellById.get(w.id))),
  ]);
}

/** Write-redirect (single): the legacy create/update path for one Borefield. */
export function upsertBorefieldIntoSpine(borefield: Borefield): void {
  upsertBorefieldsIntoSpine([borefield]);
}

/** Write-redirect (delete): remove a Borefield's pad and every well under it (legacy deleteBorefield).
 *  Matches the pad id === borefield id shim identity. */
export function removeBorefieldFromSpine(id: string): void {
  padsStore.setAll(padsStore.read().filter((p) => p.id !== id));
  wellsStore.setAll(wellsStore.read().filter((w) => w.fieldPadId !== id));
}

/** The canonical spine snapshot an FT-1 v2 import produces (mirrors parseSpineImport.SpineSnapshot;
 *  duplicated here to avoid a store→export dependency edge). */
export interface SpineSnapshotInput {
  pads: readonly Pad[];
  wells: readonly Well[];
  padJobs: readonly PadJob[];
}

/** Import-merge (Bore→Well cutover Phase 3): MERGE an imported spine snapshot into the stores with the
 *  SAME merge-not-replace-all + non-destructive discipline as `upsertBorefieldsIntoSpine`. Each entity
 *  is merged by ITS OWN id-set: an incoming pad/well/padJob REPLACES the same-id local record, and
 *  every other local record (wizard-created / carry-less wells, other pads, other campaigns) is
 *  PRESERVED (Charter Abs-7 — an import never wipes local work). Unlike the Borefield write-redirect
 *  (which filters wells by pad id because a Borefield projection can't reproduce carry-less wells),
 *  the imported snapshot is already spine-shaped, so filtering by the incoming WELL id set is both
 *  sufficient and strictly non-destructive — a local well not present in the import is kept intact. */
export function upsertSpineSnapshot(snapshot: SpineSnapshotInput): void {
  const { pads: newPads, wells: newWells, padJobs: newPadJobs } = snapshot;

  const incomingPadIds = new Set(newPads.map((p) => p.id));
  padsStore.setAll([
    ...padsStore.read().filter((p) => !incomingPadIds.has(p.id)),
    ...newPads,
  ]);

  const incomingWellIds = new Set(newWells.map((w) => w.id));
  wellsStore.setAll([
    ...wellsStore.read().filter((w) => !incomingWellIds.has(w.id)),
    ...newWells,
  ]);

  const incomingPadJobIds = new Set(newPadJobs.map((pj) => pj.id));
  padJobsStore.setAll([
    ...padJobsStore.read().filter((pj) => !incomingPadJobIds.has(pj.id)),
    ...newPadJobs,
  ]);
}
